import { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PasswordResetRequestDTO } from '@/core/api/dtos/request/auth/passwordReset'
import { useAuth } from '@/core/hooks/useAuth'

import { ResetPasswordPageUI } from './ResetPasswordPageUI'

interface ResetPasswordFormData {
	password: string
	password_confirmation: string
}

export const ResetPasswordPage = () => {
	const { email, token } = useParams<{ email: string; token: string }>()
	const { useResetPassword, login } = useAuth()
	const { t } = useTranslation('auth')
	const [apiError, setApiError] = useState<string | null>(null)

	// Validation schema
	const validationSchema = yup.object().shape({
		password: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.password').toLowerCase(),
				})
			)
			.min(8, t('register.errors.passwordMinLength')),
		password_confirmation: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.confirmPassword').toLowerCase(),
				})
			)
			.oneOf([yup.ref('password')], t('register.errors.passwordMismatch')),
	})

	const form = useForm<ResetPasswordFormData, any, ResetPasswordFormData>({
		resolver: yupResolver(validationSchema) as any,
		mode: 'onChange',
		reValidateMode: 'onChange',
	})

	const password = form.watch('password')

	const resetPasswordMutation = useResetPassword({
		options: {
			onSuccess: () => {
				login(
					{
						email: decodeURIComponent(email || ''),
						password: password,
					},
					'/zones'
				)
			},
			onError: (error: any) => {
				const errorData: any = error.response?.data
				setApiError(
					errorData?.message || 'Failed to reset password. Please try again.'
				)
			},
		},
	})

	const handleSubmit = async (values: ResetPasswordFormData) => {
		if (!email || !token) {
			setApiError('Invalid reset link. Email or token is missing.')
			return
		}

		setApiError(null)

		const payload: PasswordResetRequestDTO = {
			email: decodeURIComponent(email),
			password: values.password,
			password_confirmation: values.password_confirmation,
			token: token,
		}

		resetPasswordMutation.mutate(payload)
	}

	return (
		<ResetPasswordPageUI
			isLoading={resetPasswordMutation.isPending}
			error={apiError}
			form={form}
			onFinish={handleSubmit}
		/>
	)
}
